import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, ArrowRight, X, ChevronLeft, Sparkles, AlertCircle, Loader2, MessageSquare } from 'lucide-react';
import { trackCTA } from '@/lib/getYourAnswerApi';
import {
  searchLibrary,
  listTopics,
  describeQuestion,
  trackQuestionClick,
} from '@/lib/getYourAnswerLibraryApi';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 220;

const URGENCY_TONE = {
  high:   { label: 'High urgency', class: 'text-[#D4AF37]' },
  medium: { label: 'Common',       class: 'text-[#2FA3B5]' },
  low:    { label: 'Life event',   class: 'text-[#4C5A70]/70' },
};

export default function GetYourAnswerLibrary() {
  const [topics, setTopics] = useState([]);
  const [canonicalTotal, setCanonicalTotal] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const initialQ = searchParams.get('q') || '';
  // Topic arrives via router state (internal links stay param-free for SEO);
  // the ?topic= query param is kept as a fallback for old indexed/shared URLs.
  const initialTopic = location.state?.topic || searchParams.get('topic') || 'all';
  const navigate = useNavigate();

  const [search, setSearch] = useState(initialQ);
  const [debouncedSearch, setDebouncedSearch] = useState(initialQ);
  const [selectedTopic, setSelectedTopic] = useState(initialTopic);
  const [selectedUrgency, setSelectedUrgency] = useState('all');

  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchTookMs, setSearchTookMs] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // Load topics on mount
  useEffect(() => {
    listTopics().then((d) => {
      setTopics(d.topics || []);
      setCanonicalTotal(d.canonical_total || 0);
    });
  }, []);

  // Debounce typing
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Keep URL in sync with state
  useEffect(() => {
    const next = new URLSearchParams();
    if (debouncedSearch) next.set('q', debouncedSearch);
    if (selectedTopic !== 'all') next.set('topic', selectedTopic);
    setSearchParams(next, { replace: true });
  }, [debouncedSearch, selectedTopic, setSearchParams]);

  // Run API search whenever filters/query change. Tracks the latest request id
  // so out-of-order responses don't overwrite newer ones.
  const reqIdRef = useRef(0);
  useEffect(() => {
    const myReq = ++reqIdRef.current;
    setSearching(true);
    setSearchError(null);
    searchLibrary(debouncedSearch, {
      topic: selectedTopic,
      urgency: selectedUrgency,
      limit: PAGE_SIZE,
    }).then((r) => {
      // Only the latest in-flight request gets to update the result list,
      // but every response clears the searching flag (otherwise a stale
      // response would leave the UI hung).
      if (reqIdRef.current === myReq) {
        setResults(r.results || []);
        setSearchTookMs(r.took_ms ?? null);
        setSearchError(r.error || null);
      }
      setSearching(false);
    });
  }, [debouncedSearch, selectedTopic, selectedUrgency]);

  // Click a question → track, Link handles navigation (real <a> for crawlers)
  const trackOpen = useCallback((q) => {
    trackCTA('library-card', { question: q.slug, topic: q.topic_id });
    trackQuestionClick(q.slug);
  }, []);

  const clearAll = () => {
    setSearch('');
    setDebouncedSearch('');
    setSelectedTopic('all');
    setSelectedUrgency('all');
  };
  const hasActiveFilter = search || selectedTopic !== 'all' || selectedUrgency !== 'all';

  const isRateLimited = searchError === 'rate_limited';

  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="get-your-answer-library-page">
      <Helmet>
        <title>Question Library — Get Your Answer · KGOB</title>
        <meta
          name="description"
          content="Browse real tax questions across 23 situations. Find yours and ask a licensed CPA for a flat $95."
        />
        <meta property="og:title" content="The KGOB Question Library — real tax questions" />
        <link rel="canonical" href="https://kgob.com/get-your-answer/library" />
      </Helmet>

      <Header />

      <main>
        {/* ── Hero band ── */}
        <section className="bg-[linear-gradient(180deg,#0B2240_0%,#07182F_100%)] text-white py-7 sm:py-16">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            <Link
              to="/get-your-answer"
              className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white/85 mb-3 sm:mb-5 transition-colors"
              data-testid="library-back-link"
            >
              <ChevronLeft className="w-4 h-4" /> Back to Get Your Answer
            </Link>

            <div className="flex items-center gap-3 mb-2 sm:mb-3">
              <span className="h-px w-8 bg-[#D4AF37]" />
              <span className="font-['Inter'] text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] font-semibold">
                The Question Library
              </span>
            </div>

            <h1 className="font-['Playfair_Display'] text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-[-0.01em] mb-2 sm:mb-3">
              Real tax questions. Find yours.
            </h1>
            <p className="text-white/75 max-w-2xl text-sm sm:text-base leading-relaxed">
              {canonicalTotal > 0
                ? `${canonicalTotal} canonical questions across 23 situations — search or filter, then ask a licensed NC CPA for a flat $95.`
                : 'Search or filter to your exact spot, then ask a licensed NC CPA for a flat $95.'}
            </p>
          </div>
        </section>

        {/* ── Search + filters ── */}
        <section className="bg-white py-3 sm:py-8 border-b border-[#E1DFD7] sticky top-14 sm:top-16 z-30 backdrop-blur-md bg-white/95">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 space-y-2 sm:space-y-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4C5A70]/60" />
              <Input
                type="text"
                placeholder="Search questions — e.g. 'CP2000', 'unfiled', 'LLC', 'crypto cost basis'..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-10 h-11 bg-[#F7F6F2] border-[#E1DFD7] focus-visible:ring-[#D4AF37]"
                data-testid="library-search-input"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4C5A70]/60 hover:text-[#0B2240]"
                  aria-label="Clear search"
                  data-testid="library-search-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Topic chips — horizontal scroll rail on mobile (one row, swipeable),
                wraps on desktop. The label is hidden on mobile to save vertical
                space; sm+ shows "Topic:" prefix. */}
            <div className="flex items-center gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <span className="hidden sm:inline text-[10px] tracking-[0.18em] uppercase text-[#4C5A70]/60 font-semibold mr-1 shrink-0">Topic:</span>
              <button
                type="button"
                onClick={() => setSelectedTopic('all')}
                className={`text-xs px-3 py-1.5 rounded-full border transition-all shrink-0 ${
                  selectedTopic === 'all'
                    ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B2240] font-semibold'
                    : 'border-[#E1DFD7] text-[#4C5A70] hover:border-[#0B2240]/40'
                }`}
                data-testid="library-topic-all"
              >
                All ({canonicalTotal})
              </button>
              {topics.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setSelectedTopic(t.id)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-all shrink-0 ${
                    selectedTopic === t.id
                      ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#0B2240] font-semibold'
                      : 'border-[#E1DFD7] text-[#4C5A70] hover:border-[#0B2240]/40'
                  }`}
                  data-testid={`library-topic-${t.id}`}
                >
                  {t.label} <span className="text-[#4C5A70]/55">({t.canonical_count})</span>
                </button>
              ))}
            </div>

            {/* Urgency + result-count + clear */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 pt-0.5 sm:pt-1">
              <span className="hidden sm:inline text-[10px] tracking-[0.18em] uppercase text-[#4C5A70]/60 font-semibold">Urgency:</span>
              {['all', 'high', 'medium', 'low'].map((u) => (
                <button
                  key={u}
                  type="button"
                  onClick={() => setSelectedUrgency(u)}
                  className={`text-xs px-2 sm:px-2.5 py-1 rounded transition-colors ${
                    selectedUrgency === u ? 'text-[#0B2240] font-semibold underline underline-offset-4' : 'text-[#4C5A70] hover:text-[#0B2240]'
                  }`}
                  data-testid={`library-urgency-${u}`}
                >
                  {u === 'all' ? 'All' : u === 'high' ? 'High' : u === 'medium' ? 'Common' : 'Life'}
                </button>
              ))}

              <span className="ml-auto text-xs text-[#4C5A70] flex items-center gap-1.5 sm:gap-2" data-testid="library-result-count">
                {searching ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin" /> searching…
                  </>
                ) : (
                  <>
                    <strong className="text-[#0B2240]">{results.length}</strong> result{results.length === 1 ? '' : 's'}
                    {searchTookMs != null && <span className="hidden sm:inline text-[#4C5A70]/50"> · {searchTookMs}ms</span>}
                  </>
                )}
              </span>
              {hasActiveFilter && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-xs text-[#B45F3E] hover:text-[#8A3F25] underline underline-offset-4"
                  data-testid="library-clear-filters"
                >
                  Clear all filters
                </button>
              )}
            </div>
          </div>
        </section>

        {/* ── Results ── */}
        <section className="py-10 sm:py-14">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
            {isRateLimited && (
              <div className="text-center py-12" data-testid="library-rate-limited">
                <AlertCircle className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
                <p className="text-[#0B2240] text-lg font-semibold mb-2">Taking a quick breather.</p>
                <p className="text-[#4C5A70] text-sm mb-1">You searched a lot — give it a minute and try again.</p>
              </div>
            )}

            {!isRateLimited && !searching && results.length === 0 && (
              <NoResults
                query={debouncedSearch}
                onClear={clearAll}
                data-testid="library-empty"
              />
            )}

            {!isRateLimited && results.length > 0 && (
              <ul className="space-y-2" data-testid="library-results-list">
                {results.map((q) => {
                  const tone = URGENCY_TONE[q.urgency] || URGENCY_TONE.medium;
                  return (
                    <li key={q.id}>
                      <Link
                        to={`/get-your-answer/${(q.topic_id || '').replace(/_/g, '-')}/${q.slug}`}
                        onClick={() => trackOpen(q)}
                        className="group w-full text-left p-4 sm:p-5 rounded-xl bg-white border border-[#E1DFD7] hover:border-[#D4AF37] hover:shadow-sm transition-all flex items-start gap-4"
                        data-testid={`library-question-${q.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0B2240] text-sm sm:text-base leading-snug">
                            &ldquo;{q.text}&rdquo;
                          </p>
                          <div className="mt-2 flex items-center gap-3 text-[10px] tracking-[0.16em] uppercase font-semibold flex-wrap">
                            <span className={tone.class}>{tone.label}</span>
                            <span className="text-[#4C5A70]/40">&middot;</span>
                            <span className="text-[#4C5A70]/70">{q.topic_label || q.topic_id}</span>
                            {q.matched_variant && (
                              <>
                                <span className="text-[#4C5A70]/40">&middot;</span>
                                <span className="text-[#0B2240]/65 normal-case tracking-normal font-normal">
                                  matched: &ldquo;{q.matched_variant.slice(0, 70)}{q.matched_variant.length > 70 ? '…' : ''}&rdquo;
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                        <span className="hidden sm:flex items-center gap-1 text-xs text-[#D4AF37] font-semibold tracking-[0.12em] uppercase flex-shrink-0 mt-1 group-hover:gap-2 transition-all">
                          Open <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* ── Bottom CTA strip ── */}
        <section className="bg-[#0B2240] text-white py-14">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
            <Sparkles className="w-6 h-6 text-[#D4AF37] mx-auto mb-3" />
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl leading-tight mb-3">
              Didn&rsquo;t find your exact situation?
            </h2>
            <p className="text-white/75 mb-6 text-sm sm:text-base">
              Browse the topics or describe your question in your own words on the booking page.
            </p>
            <Link
              to="/get-your-answer"
              onClick={() => trackCTA('library-bottom-cta', {})}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-xl bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold shadow-[inset_0_1px_0_rgba(255,255,255,0.35)] hover:brightness-110"
              data-testid="library-bottom-cta"
            >
              Back to Get Your Answer <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// "Describe your question" capture (only shown when a search returns 0 results)
// ──────────────────────────────────────────────────────────────────────────────

function NoResults({ query, onClear }) {
  const navigate = useNavigate();
  const [description, setDescription] = useState(query || '');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill description from the searched query so the user doesn't retype it
  useEffect(() => {
    if (query && query !== description) setDescription(query);
  }, [query]);

  const submit = async () => {
    const text = description.trim();
    if (text.length < 10) return;
    setSubmitting(true);
    // Fire-and-forget the log (also done on the ask page on mount, but here we
    // can attach the email which the ask page can't because it only has the query
    // string). Then route to the ask page where the user sees a proper question
    // page with the booking CTA.
    if (email.trim()) {
      await describeQuestion(text, email.trim());
    }
    navigate(`/get-your-answer/ask?q=${encodeURIComponent(text)}`);
  };

  return (
    <div className="max-w-2xl mx-auto text-center py-12" data-testid="library-empty">
      <AlertCircle className="w-8 h-8 text-[#D4AF37] mx-auto mb-3" />
      <p className="text-[#0B2240] text-lg font-semibold mb-2">
        {query ? `We couldn't find a great match for "${query}"` : 'No questions match those filters.'}
      </p>
      <p className="text-[#4C5A70] text-sm mb-7">
        Bring it to a CPA in your own words — they&rsquo;ll handle it.
      </p>

      <div className="text-left bg-white border border-[#E1DFD7] rounded-xl p-5 sm:p-6 shadow-sm">
        <div className="flex items-center gap-2 text-[#0B2240] mb-3">
          <MessageSquare className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-xs tracking-[0.18em] uppercase font-semibold">Describe your question</span>
        </div>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. I need to pay my California FTB estimated taxes — can I do it online and what's the safest way?"
          className="w-full min-h-[110px] p-3 rounded-lg border border-[#E1DFD7] bg-[#F7F6F2] text-sm text-[#0B2240] focus:outline-none focus:border-[#D4AF37]"
          maxLength={600}
          data-testid="library-describe-textarea"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (optional, if you want us to follow up)"
          className="w-full mt-3 p-3 rounded-lg border border-[#E1DFD7] bg-[#F7F6F2] text-sm text-[#0B2240] focus:outline-none focus:border-[#D4AF37]"
          data-testid="library-describe-email"
        />
        <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-[11px] text-[#4C5A70]/70">
            We&rsquo;ll take you to your question page where you can book a session with a CPA.
          </p>
          <Button
            onClick={submit}
            disabled={submitting || description.trim().length < 10}
            className="bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240] font-semibold hover:brightness-110"
            data-testid="library-describe-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Take me there
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>

      <div className="mt-6">
        <Button onClick={onClear} variant="outline" className="border-[#D4AF37] text-[#D4AF37]" data-testid="library-empty-clear">
          Clear filters
        </Button>
      </div>
    </div>
  );
}
